import React, { useEffect, useState } from 'react';
import { ListChecks, Plus, Trash2, Pencil, Loader2 } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import type { Watchlist } from '../../types/strategyLab';
import WatchlistBuilder from '../../components/strategy-lab/WatchlistBuilder';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const WatchlistsPage: React.FC = () => {
  const watchlists = useStrategyLabStore((s) => s.watchlists);
  const loading = useStrategyLabStore((s) => s.watchlistsLoading);
  const error = useStrategyLabStore((s) => s.watchlistsError);
  const fetchWatchlists = useStrategyLabStore((s) => s.fetchWatchlists);
  const deleteWatchlist = useStrategyLabStore((s) => s.deleteWatchlist);

  const [mode, setMode] = useState<'idle' | 'create' | 'edit'>('idle');
  const [editing, setEditing] = useState<Watchlist | null>(null);

  useEffect(() => {
    fetchWatchlists();
  }, [fetchWatchlists]);

  const startCreate = () => {
    setEditing(null);
    setMode('create');
  };
  const startEdit = (wl: Watchlist) => {
    setEditing(wl);
    setMode('edit');
  };
  const finishBuilder = () => {
    setMode('idle');
    setEditing(null);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
      {/* List column */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary" /> Watchlists
          </h2>
          <Button size="sm" onClick={startCreate}>
            <Plus className="w-4 h-4" /> New
          </Button>
        </div>

        {error && <p className="text-sm text-rose-500">{error}</p>}

        {loading && watchlists.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading watchlists…
          </div>
        ) : watchlists.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground text-sm">
              No watchlists yet. Create one to group symbols for scanners.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {watchlists.map((wl) => (
              <Card
                key={wl.id}
                className={`transition-colors ${editing?.id === wl.id ? 'border-primary' : ''}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-base">{wl.name}</CardTitle>
                      {wl.description && (
                        <CardDescription className="mt-0.5">{wl.description}</CardDescription>
                      )}
                    </div>
                    <Badge variant="secondary" className="shrink-0">
                      {wl.symbol_count} symbol{wl.symbol_count === 1 ? '' : 's'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(wl)}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-rose-500 hover:text-rose-600"
                    onClick={() => {
                      if (window.confirm(`Delete watchlist “${wl.name}”?`)) {
                        deleteWatchlist(wl.id);
                        if (editing?.id === wl.id) finishBuilder();
                      }
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Builder column */}
      <div>
        {mode === 'idle' ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center text-muted-foreground text-sm space-y-3">
              <p>Select a watchlist to edit, or create a new one.</p>
              <Button variant="outline" size="sm" onClick={startCreate}>
                <Plus className="w-4 h-4" /> New watchlist
              </Button>
            </CardContent>
          </Card>
        ) : (
          <WatchlistBuilder editing={mode === 'edit' ? editing : null} onDone={finishBuilder} />
        )}
      </div>
    </div>
  );
};

export default WatchlistsPage;
